import * as React from "react";

import { useHotkey, type Hotkey } from "@/shared/foundation/primitives/Hotkey";

/**
 * The shortcut that opens the palette. Exported so the trigger's `<Kbd>` and
 * its tooltip render the same binding this hook registers — one source of
 * truth, so the label cannot drift from the behavior.
 */
export const QUICK_SEARCH_HOTKEY: Hotkey = "Mod+K";

/**
 * Owns the open/closed state of a quick-search palette and binds
 * {@link QUICK_SEARCH_HOTKEY} to open it — the command-palette convention;
 * the browser's native Cmd/Ctrl+F find stays untouched.
 *
 * `open` opens the palette from a pointer affordance (e.g.
 * `<QuickSearchButton>`); the hotkey opens it directly.
 *
 * The shortcut deliberately fires while focus is inside a text input — that
 * is the library's default for Ctrl/Meta combinations, and it's what lets a
 * user jump into the palette from a focused search box.
 *
 * Takes no `enabled` gate. An earlier design had one so embedded lists could
 * decline the binding, but that existed only while the shortcut was Cmd/Ctrl+F
 * and would have stolen the browser's find. On Mod+K there is nothing to
 * decline: sibling lists are never mounted together (a collection renders one
 * list for its `resource_type`; dataset tabs are routes), so no two palettes
 * compete for the key. Should a surface ever need to opt out, the foundation
 * `useHotkey` still takes `enabled` — pass it through then.
 *
 * Shape mirrors `usePopupState`: `isOpen` is the state, `open`/`close` are
 * the actions.
 */
export function useQuickSearchHotkey(): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = React.useState(false);

  useHotkey(QUICK_SEARCH_HOTKEY, () => setIsOpen(true));

  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  return { isOpen, open, close };
}
